import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    getHourlyEditContext,
    updateHourlyInvoices,
} from '../../../../services/HourlyInvoiceEditService'
import {
    combinable,
    ledgerFor,
    partyBreakdownFor,
    resolveShare,
} from '../similarity'

/**
 * State for the hourly invoice EDIT wizard.
 *
 * DELIBERATELY A SEPARATE MODULE FROM THE FIXED EDITOR
 * `pages/UpdateInvoice.jsx` owns the fixed flow and is not touched by this
 * work. The shared, stateless pieces (PartySelectionCard, the HourlyInvoice
 * panes) are reused; the state is not.
 *
 * THE UNIT OF EDITING IS AN INVOICE ROW, NOT A PARTY
 * Over half of live cases carry more than one hourly invoice — one has eight,
 * and a single party can hold six. "Edit the plaintiff's hourly invoice" does
 * not name a row. So step 1 picks a BILLING RUN (a set of invoices raised
 * together) and then which side(s) of it to edit; everything downstream carries
 * `hourly_invoice_payment.id`.
 */

const HourlyUpdateInvoiceContext = createContext(null)

export const useHourlyUpdateInvoice = () => useContext(HourlyUpdateInvoiceContext)

/**
 * Which billing run a row belongs to.
 *
 * Keyed on WHEN IT WAS RAISED, not on invoice_history_id. History ids are not
 * stable: an edit can fork a shared session so that four invoices raised
 * together end up on four separate copies of it. Keying on the history id then
 * split them into four "runs" of one invoice each, and the wizard would only
 * ever offer one party. `created_at` does not move when an invoice is reissued.
 *
 * Truncated to the MINUTE because a run is written row by row: case 2762's two
 * invoices are a second apart, and a second-level key would separate them.
 */
const groupKeyFor = (row) => `t:${String(row?.created_at || '').slice(0, 16)}`

const PAID_STATUSES = ['paid', 'paid_manually']

/**
 * Why a given invoice cannot be edited, or null when it can.
 *
 * The server's own verdict wins when it sent one (`editable` /
 * `blocked_reason`), so the UI can never offer an action the API will refuse.
 * The status checks below are the fallback for a response that predates those
 * fields.
 */
export const blockedReasonFor = (row) => {
    if (!row) return 'Invoice not found.'
    if (row.editable === false) return row.blocked_reason || 'Not editable'
    if (row.status === 'paid') return 'Already paid'
    if (row.status === 'paid_manually') return 'Marked paid'
    if (row.status === 'held') return 'Awaiting approval'
    return null
}

/**
 * `invoice_history.items` arrives as a JSON string on some rows, an array on
 * others, an object on a few, and null on roughly a quarter of them.
 */
const parseMaybeJson = (value) => {
    if (Array.isArray(value)) return value
    if (typeof value !== 'string') return null
    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : null
    } catch (_) {
        return null
    }
}

/**
 * The ledger one invoice opens with.
 *
 * `invoice_history.items` when stored. About a quarter of live rows never
 * stored theirs — those open on a single line rebuilt from `amount -
 * admin_fee`, flagged `_recovered` so Step 2 can say it was reconstructed
 * rather than loaded.
 */
const deriveItemsFor = (row) => {
    const parsed = parseMaybeJson(row?.invoice_history?.items)

    if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((it, i) => ({
            id: it?.id ?? i + 1,
            description: it?.description || '',
            quantity: Number(it?.quantity) || 1,
            rate: Number(it?.rate) || 0,
            amount: Number(it?.amount) || 0,
            _recovered: false,
        }))
    }

    const base = Math.max(0, Number(row?.amount || 0) - Number(row?.admin_fee || 0))
    return [
        {
            id: 1,
            description: 'Mediation Session',
            quantity: 1,
            rate: base,
            amount: base,
            _recovered: true,
        },
    ]
}

/**
 * Is this a SYNTHETIC party-scope ledger rather than a real session ledger?
 *
 * The party-scope seed below collapses one party's share into a single line and
 * tags it `_derivedFromShare`. That line is worth ONE PARTY'S BILL, not the
 * session — so it must never be reused as the anchor for a shared, case-wide
 * edit. `setSelection` prunes the per-invoice maps by id, not by scope, so the
 * entry survives a "one party -> back -> all parties" trip and would otherwise
 * be the first thing the shared branch finds.
 */
const isPartyScopeLedger = (items) =>
    Array.isArray(items) && items.some((it) => it?._derivedFromShare)

export const HourlyUpdateInvoiceProvider = ({
    caseId,
    onClose,
    /* The router already fetched the context to decide fixed-vs-hourly. Passing
     * it in avoids a second round trip and a second spinner. */
    initialContext = null,
    children,
}) => {
    const [currentStep, setCurrentStep] = useState(1)
    const [loading, setLoading] = useState(!initialContext)
    const [error, setError] = useState('')
    const [toast, setToast] = useState(null)

    const [caseInfo, setCaseInfo] = useState(initialContext?.case || null)
    const [mediatorRecord, setMediatorRecord] = useState(initialContext?.mediator || null)
    const [invoiceRows, setInvoiceRows] = useState(
        Array.isArray(initialContext?.invoices) ? initialContext.invoices : []
    )

    // Step 1 selections
    const [selectedGroupKey, setSelectedGroupKey] = useState(null)
    /* The unit of selection is an INVOICE, not a side. A 'plaintiff' |
     * 'defendant' | 'both' model can never target more than two invoices — on a
     * case with two plaintiffs and two defendants it silently edits two of the
     * four and leaves the other two billed against a session that no longer
     * exists. */
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([])
    const [selectionMode, setSelectionMode] = useState(null) // 'one' | 'all' | null
    /* 'session' edits the case-wide mediation ledger every selected party
     * shares. 'party' edits ONE party's own bill and leaves the others alone. */
    const [editScope, setEditScope] = useState('session')

    // Step 2 — keyed by hourly_invoice_payment.id
    const [itemsByInvoice, setItemsByInvoice] = useState({})
    const [dueDateByInvoice, setDueDateByInvoice] = useState({})
    const [notesByInvoice, setNotesByInvoice] = useState({})
    const [clientInfoByInvoice, setClientInfoByInvoice] = useState({})

    // Step 3
    const [submitting, setSubmitting] = useState(false)
    const [results, setResults] = useState(null)
    /* Ref guard: a fast double-click can fire the submit twice before React
     * re-renders the disabled button, and each fire voids and reissues a live
     * Stripe invoice. The server carries its own in-flight guard (409) — this
     * is the cheap first line. */
    const submittingRef = useRef(false)
    /* Held in a ref so the redirect timer cannot capture a stale closure. */
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    const notify = useCallback((type, message) => setToast({ type, message }), [])

    useEffect(() => {
        if (!toast) return undefined
        const t = setTimeout(() => setToast(null), 5000)
        return () => clearTimeout(t)
    }, [toast])

    /* ---------------------------------------------------------------- load */

    const load = useCallback(async () => {
        if (!caseId) return
        try {
            setLoading(true)
            setError('')
            const data = await getHourlyEditContext(caseId)
            if (!data?.case) {
                setError('Case not found')
                return
            }
            setCaseInfo(data.case)
            setMediatorRecord(data.mediator || null)
            setInvoiceRows(Array.isArray(data.invoices) ? data.invoices : [])
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Unable to load invoices for this case.'
            )
        } finally {
            setLoading(false)
        }
    }, [caseId])

    useEffect(() => {
        if (!initialContext) load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load])

    /* ------------------------------------------------------- derived model */

    /** Which side of the case a row belongs to, via the NESTED case parties. */
    const sideOf = useCallback(
        (row) => {
            const cid = row?.client_id
            if (!cid) return null
            if (caseInfo?.plaintiff?.client_id === cid) return 'plaintiff'
            if (caseInfo?.defendant?.client_id === cid) return 'defendant'
            if (
                (caseInfo?.plaintiff?.additionalParties || []).some(
                    (p) => p?.client_id === cid
                )
            ) {
                return 'plaintiff'
            }
            if (
                (caseInfo?.defendant?.additionalParties || []).some(
                    (p) => p?.client_id === cid
                )
            ) {
                return 'defendant'
            }
            return null
        },
        [caseInfo]
    )

    /** Invoices grouped into billing runs, newest first. */
    const groups = useMemo(() => {
        const map = new Map()
        for (const row of invoiceRows) {
            const key = groupKeyFor(row)
            if (!map.has(key)) map.set(key, { key, rows: [], created_at: row.created_at })
            map.get(key).rows.push({ ...row, side: sideOf(row) })
        }
        return [...map.values()]
            .map((g) => ({
                ...g,
                total: g.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
                editableCount: g.rows.filter((r) => !blockedReasonFor(r)).length,
            }))
            .sort((a, b) =>
                String(b.created_at || '').localeCompare(String(a.created_at || ''))
            )
    }, [invoiceRows, sideOf])

    const selectedGroup = useMemo(
        () => groups.find((g) => g.key === selectedGroupKey) || null,
        [groups, selectedGroupKey]
    )

    /**
     * Every party that could be edited, plaintiffs first.
     *
     * Scoped to the selected billing RUN — and to groups[0] when nothing is
     * pinned, NOT to every row on the case. The run picker DISPLAYS groups[0]
     * by default while selectedGroupKey stays null, so pooling every row made
     * "all parties" target invoices from runs nobody chose, and made
     * combinable() compare invoices raised weeks apart.
     *
     * A row whose side cannot be resolved is excluded here and surfaced
     * separately — silently dropping it is how GSC cases became uneditable.
     */
    const candidateRows = useMemo(() => {
        const pool = selectedGroup ? selectedGroup.rows : groups[0]?.rows ?? invoiceRows
        const rank = (r) =>
            (r.side === 'plaintiff' ? 0 : r.side === 'defendant' ? 2 : 4) +
            (r.isPrimary ? 0 : 1)
        return pool
            .map((r) => ({ ...r, side: sideOf(r) }))
            .filter((r) => !blockedReasonFor(r) && r.side)
            .sort((a, b) => rank(a) - rank(b))
    }, [selectedGroup, groups, invoiceRows, sideOf])

    /** Editable rows we could not attribute to a side — shown, never dropped. */
    const unresolvedRows = useMemo(() => {
        const pool = selectedGroup ? selectedGroup.rows : groups[0]?.rows ?? invoiceRows
        return pool
            .map((r) => ({ ...r, side: sideOf(r) }))
            .filter((r) => !blockedReasonFor(r) && !r.side)
    }, [selectedGroup, groups, invoiceRows, sideOf])

    /** How many OTHER invoices share this row's stored session breakdown. */
    const sharedSiblingCountFor = useCallback(
        (row) =>
            row?.invoice_history_id == null
                ? 0
                : invoiceRows.filter(
                      (r) =>
                          String(r.invoice_history_id) ===
                              String(row.invoice_history_id) &&
                          String(r.id) !== String(row.id)
                  ).length,
        [invoiceRows]
    )

    /**
     * THE ONE PLACE every screen asks what a party is billed.
     *
     * In party scope the ledger on screen IS that party's bill, so the share is
     * 1 by construction. Calling partyBreakdownFor directly anywhere else would
     * re-resolve the stored fraction against a private ledger and show a
     * quarter of the party's own invoice.
     */
    const breakdownFor = useCallback(
        (row, ledgerTotal) =>
            editScope === 'party'
                ? partyBreakdownFor(row, caseInfo, ledgerTotal, {
                      share: 1,
                      adminInAmount: resolveShare(row, caseInfo).adminInAmount,
                  })
                : partyBreakdownFor(row, caseInfo, ledgerTotal),
        [editScope, caseInfo]
    )

    /** Can every candidate be edited as one bill? Drives the "all parties" card. */
    const combinedGate = useMemo(
        () =>
            candidateRows.length > 1
                ? combinable(candidateRows, caseInfo)
                : { ok: false, reason: 'Only one invoice can be edited on this case.' },
        [candidateRows, caseInfo]
    )

    /** The rows the current selection actually targets. */
    const targetRows = useMemo(
        () =>
            candidateRows.filter((r) =>
                selectedInvoiceIds.map(String).includes(String(r.id))
            ),
        [candidateRows, selectedInvoiceIds]
    )

    /* --------------------------------------------------------- step 2 seed */

    const seedItemsForTargets = useCallback(
        (rows, { shared = false, party = false } = {}) => {
            /* PARTY SCOPE — the editor shows this party's OWN bill.
             *
             * Gated on share < 1, NOT on scope alone. When the share is already
             * 1 the stored ledger IS this party's bill and must open verbatim:
             * collapsing it into one synthetic line would destroy the mediator's
             * own line items on every sole-owner invoice, which is roughly half
             * of all hourly rows. Only a genuine fraction gets rebuilt, and it
             * is rebuilt from the base — hours and rate are never divided. */
            if (party && rows.length === 1) {
                const row = rows[0]
                const b = partyBreakdownFor(row, caseInfo, ledgerFor(row).sum)
                if (Math.abs(b.share - 1) > 1e-4 && b.base > 0) {
                    setItemsByInvoice((prev) => ({
                        ...prev,
                        [row.id]: [
                            {
                                id: 1,
                                description: `Mediation Session — ${b.pct}% share`,
                                quantity: 1,
                                rate: b.base,
                                amount: b.base,
                                _recovered: true,
                                _derivedFromShare: true,
                            },
                        ],
                    }))
                    setDueDateByInvoice((prev) => ({
                        ...prev,
                        [row.id]:
                            prev[row.id] ??
                            (row.due_date ? String(row.due_date).slice(0, 10) : ''),
                    }))
                    return
                }
                // share === 1: fall through and open the stored ledger unchanged.
            }

            /* Under a shared edit every selected invoice must open on the SAME
             * ledger. Seeding each row from its own stored items would hand a
             * party added mid-edit its pre-edit ledger while its siblings carry
             * the edited one, and the single editor would then submit two
             * different ledgers. */
            if (shared && rows.length > 1) {
                setItemsByInvoice((prev) => {
                    /* The anchor must be a SESSION ledger. A party-scope seed is
                     * one party's share collapsed into a single line, and
                     * `setSelection` keeps it alive across the switch to "all
                     * parties" because the row is still selected. Taking it as
                     * the anchor copies one party's bill onto every invoice as
                     * though it were the whole session: a 3-party $2,000 session
                     * billed 50% each becomes a $1,000 ledger, each party is
                     * rebilled $500, and every live Stripe invoice is voided and
                     * reissued at half — from a click-through in which the case
                     * manager never edited a line. */
                    const anchor =
                        rows
                            .map((r) => prev[r.id])
                            .find(
                                (v) =>
                                    Array.isArray(v) &&
                                    v.length &&
                                    !isPartyScopeLedger(v)
                            ) ||
                        deriveItemsFor(rows[0])
                    const next = { ...prev }
                    for (const row of rows) next[row.id] = anchor.map((it) => ({ ...it }))
                    return next
                })
                setDueDateByInvoice((prev) => {
                    const anchor =
                        rows.map((r) => prev[r.id]).find((v) => v !== undefined) ??
                        (rows[0].due_date ? String(rows[0].due_date).slice(0, 10) : '')
                    const next = { ...prev }
                    for (const row of rows) next[row.id] = anchor
                    return next
                })
                setNotesByInvoice((prev) => {
                    const anchor =
                        rows.map((r) => prev[r.id]).find((v) => v !== undefined) ?? ''
                    const next = { ...prev }
                    for (const row of rows) next[row.id] = anchor
                    return next
                })
                return
            }

            setItemsByInvoice((prev) => {
                const next = { ...prev }
                for (const row of rows) {
                    if (next[row.id]) continue // keep edits when stepping back
                    next[row.id] = deriveItemsFor(row)
                }
                return next
            })

            setDueDateByInvoice((prev) => {
                const next = { ...prev }
                for (const row of rows) {
                    if (next[row.id] === undefined) {
                        next[row.id] = row.due_date
                            ? String(row.due_date).slice(0, 10)
                            : ''
                    }
                }
                return next
            })
        },
        // caseInfo is read by the party-scope branch above.
        [caseInfo]
    )

    /* ------------------------------------------------------------ handlers */

    /* Changing the run changes WHICH invoices are being edited. The per-invoice
     * maps are keyed by invoice id, so stale entries would otherwise survive
     * and — because buildPayload reads targetRows — could submit line items
     * entered for a different invoice. */
    const selectGroup = (key) => {
        setSelectedGroupKey(key)
        setSelectedInvoiceIds([])
        setSelectionMode(null)
        setItemsByInvoice({})
        setDueDateByInvoice({})
        setNotesByInvoice({})
        setClientInfoByInvoice({})
        setResults(null)
    }

    /**
     * Change which invoices are being edited.
     *
     * PRUNES rather than wipes — toggling between one party and all of them
     * used to throw away a ledger already typed.
     *
     * REFUSES once anything has succeeded. After a partial failure the retry
     * sends only the failed ids; dropping a row from the selection would make
     * that filter match nothing, and re-selecting then resubmitting would void
     * an invoice that was just reissued and email the party a second time.
     */
    const setSelection = (ids, mode = null) => {
        if (Array.isArray(results) && results.some((r) => r.success)) {
            notify(
                'warning',
                'Some invoices have already been sent. Reload the case to start a new edit.'
            )
            return
        }
        const next = (ids || []).map(String)
        setSelectedInvoiceIds(next)
        setSelectionMode(mode)
        /* SCOPE FOLLOWS THE SELECTION. It is not a second question.
         *
         * "Send to one party" MEANS edit that party's own bill — their amount,
         * their lines, no split and no "TOTAL DUE BY EACH PARTY" credit. Making
         * that an extra opt-in was the mistake: someone who picked one party
         * still got the whole four-way session because they had not clicked a
         * second control they had no reason to look for. */
        setEditScope(mode === 'one' ? 'party' : 'session')

        const keep = (prev) =>
            Object.fromEntries(
                Object.entries(prev).filter(([id]) => next.includes(String(id)))
            )
        setItemsByInvoice(keep)
        setDueDateByInvoice(keep)
        setNotesByInvoice(keep)
        // clientInfoByInvoice is deliberately NOT pruned — Step 1 reseeds it,
        // and clearing it would discard a bill-to just corrected.
    }

    const handleStep1Continue = () => {
        if (selectedInvoiceIds.length === 0) {
            return notify('warning', 'Select which invoice to update.')
        }
        if (targetRows.length === 0) {
            return notify(
                'warning',
                'That party has no editable invoice — theirs is already paid.'
            )
        }
        /* Re-checked HERE, not only when the option was first offered: changing
         * the selection afterwards could leave the combined option lit over a
         * set that is no longer one bill. */
        if (targetRows.length > 1) {
            const gate = combinable(targetRows, caseInfo)
            if (!gate.ok) return notify('warning', gate.reason)
        }
        seedItemsForTargets(targetRows, {
            shared: targetRows.length > 1,
            party: editScope === 'party',
        })
        setCurrentStep(2)
        return undefined
    }

    const setItemsFor = (invoiceId, items) =>
        setItemsByInvoice((prev) => ({ ...prev, [invoiceId]: items }))

    const setDueDateFor = (invoiceId, value) =>
        setDueDateByInvoice((prev) => ({ ...prev, [invoiceId]: value }))

    const setNotesFor = (invoiceId, value) =>
        setNotesByInvoice((prev) => ({ ...prev, [invoiceId]: value }))

    /* ONE editor, N invoices.
     *
     * The per-invoice maps stay the payload's source of truth — buildPayload
     * and the retry path both read them per row, and a retry reads a SUBSET. A
     * single shared blob would force a mode branch inside exactly the code path
     * where a wrong read voids a live Stripe invoice. So the fan-out happens at
     * the setter and is invisible downstream. */
    const writeToAll = (setter) => (value) =>
        setter((prev) => {
            const next = { ...prev }
            for (const id of selectedInvoiceIds) next[id] = value
            return next
        })

    const setItemsForAll = writeToAll(setItemsByInvoice)
    const setDueDateForAll = writeToAll(setDueDateByInvoice)
    const setNotesForAll = writeToAll(setNotesByInvoice)

    const setClientInfoFor = (invoiceId, value) =>
        setClientInfoByInvoice((prev) => ({ ...prev, [invoiceId]: value }))

    const subtotalFor = useCallback(
        (invoiceId) =>
            (itemsByInvoice[invoiceId] || []).reduce(
                (s, it) => s + (Number(it?.amount) || 0),
                0
            ),
        [itemsByInvoice]
    )

    const handleStep2Continue = () => {
        for (const row of targetRows) {
            const items = itemsByInvoice[row.id] || []
            if (items.length === 0) {
                return notify('warning', 'Each invoice needs at least one line item.')
            }
            if (items.some((it) => !String(it.description || '').trim())) {
                return notify('warning', 'Every line item needs a description.')
            }
            if (subtotalFor(row.id) <= 0) {
                return notify('warning', 'Each invoice total must be greater than zero.')
            }
        }
        setCurrentStep(3)
        return undefined
    }

    const handleBack = () => setCurrentStep((s) => Math.max(1, s - 1))

    /**
     * The exact body the endpoint expects.
     *
     * `only` narrows it to a subset of invoice ids. Retry after a partial
     * failure MUST pass the failed ids: every submission voids and reissues, so
     * resending a row that already succeeded would void the invoice we just
     * created and email the party a second one.
     */
    const buildPayload = (only = null) => ({
        case_id: Number(caseId),
        /* Derived from what is actually being sent, so the wire can never
         * disagree with invoices[]. The controller documents this field as
         * informational, so 'both' stays the literal even for 3+ parties. */
        selectedOption: targetRows.length > 1 ? 'both' : 'one',
        invoices: (only
            ? targetRows.filter((row) => only.map(String).includes(String(row.id)))
            : targetRows
        ).map((row) => ({
            hourly_invoice_id: row.id,
            items: (itemsByInvoice[row.id] || []).map((it) => ({
                id: it.id,
                description: it.description,
                quantity: Number(it.quantity) || 0,
                rate: Number(it.rate) || 0,
                amount: Number(it.amount) || 0,
            })),
            due_date: dueDateByInvoice[row.id] || null,
            /* Party scope declares 'these items ARE this party's bill'. The
             * server forces share 1 on it rather than trusting a fraction off
             * the wire. */
            ...(editScope === 'party'
                ? {
                      scope: 'party',
                      party_share: {
                          share: 1,
                          admin_in_amount: resolveShare(row, caseInfo).adminInAmount,
                      },
                  }
                : {}),
            additional_notes: notesByInvoice[row.id] ?? null,
            invoice_address:
                clientInfoByInvoice[row.id]?.client_information?.address ?? null,
            client_information:
                clientInfoByInvoice[row.id]?.client_information ?? null,
        })),
    })

    /**
     * Submit. Lives here rather than in Step 3 because the action bar that
     * triggers it belongs to the shell.
     */
    const submit = async () => {
        if (submittingRef.current) return
        submittingRef.current = true

        /* Retry sends ONLY what failed — resending a success would void the
         * invoice we just issued and email the party again. */
        const retryIds = Array.isArray(results)
            ? results.filter((r) => !r.success).map((r) => r.hourly_invoice_id)
            : null

        try {
            setSubmitting(true)
            const body = await updateHourlyInvoices(caseId, buildPayload(retryIds))
            const list = Array.isArray(body?.results) ? body.results : []

            // Merge so earlier successes stay visible through a retry.
            setResults((prev) => {
                if (!Array.isArray(prev)) return list
                const merged = new Map(prev.map((r) => [String(r.hourly_invoice_id), r]))
                for (const r of list) merged.set(String(r.hourly_invoice_id), r)
                return [...merged.values()]
            })

            const failed = list.filter((r) => !r.success)
            if (list.length === 0) {
                /* 200 with no results[] is not a success — say so rather than
                 * bouncing back to the case as though the invoices went out. */
                notify(
                    'error',
                    body?.message ||
                        'The invoice service returned no result. Reload the case and check the Finances table before trying again.'
                )
            } else if (failed.length === 0) {
                notify(
                    'success',
                    list.length === 1
                        ? 'Invoice has been updated successfully.'
                        : `${list.length} invoices have been updated successfully.`
                )
                /* Back to the case once everything landed — the Finances table
                 * there is where this gets confirmed. Same 1.5s the fixed
                 * editor uses after its own update. */
                setTimeout(() => {
                    onCloseRef.current?.()
                }, 1500)
            } else {
                notify(
                    'error',
                    `${failed.length} of ${list.length} invoices could not be updated.`
                )
            }
        } catch (err) {
            const status = err?.response?.status
            const message = err?.response?.data?.message
            if (status === 409) {
                /* Already running server-side. Auto-retrying would void the
                 * invoice the other request is in the middle of issuing. */
                notify(
                    'warning',
                    message ||
                        'This invoice update is already being processed. Wait for it to finish before submitting again.'
                )
            } else {
                notify(
                    'error',
                    message || 'Could not update the invoice. Nothing was changed.'
                )
            }
        } finally {
            setSubmitting(false)
            submittingRef.current = false
        }
    }

    const allSucceeded =
        Array.isArray(results) && results.length > 0 && results.every((r) => r.success)
    const failedCount = Array.isArray(results)
        ? results.filter((r) => !r.success).length
        : 0

    const value = {
        // lifecycle
        caseId,
        onClose,
        loading,
        error,
        reload: load,
        toast,
        setToast,
        notify,
        // data
        caseInfo,
        mediatorRecord,
        invoiceRows,
        groups,
        selectedGroup,
        targetRows,
        blockedReasonFor,
        PAID_STATUSES,
        // step 1
        selectedGroupKey,
        setSelectedGroupKey: selectGroup,
        selectedInvoiceIds,
        selectionMode,
        setSelection,
        editScope,
        sharedSiblingCountFor,
        breakdownFor,
        candidateRows,
        unresolvedRows,
        combinedGate,
        clientInfoByInvoice,
        setClientInfoFor,
        handleStep1Continue,
        // step 2
        itemsByInvoice,
        setItemsFor,
        setItemsForAll,
        dueDateByInvoice,
        setDueDateFor,
        setDueDateForAll,
        notesByInvoice,
        setNotesFor,
        setNotesForAll,
        subtotalFor,
        handleStep2Continue,
        // step 3
        submitting,
        results,
        buildPayload,
        submit,
        allSucceeded,
        failedCount,
        navigateToCase: onClose,
        // nav
        currentStep,
        setCurrentStep,
        handleBack,
    }

    return (
        <HourlyUpdateInvoiceContext.Provider value={value}>
            {children}
        </HourlyUpdateInvoiceContext.Provider>
    )
}

export default HourlyUpdateInvoiceContext
